import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  HostBinding,
  ViewEncapsulation,
  ElementRef,
} from '@angular/core';
import { ChartConfiguration, ChartOptions } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep, isNil } from 'lodash';
import { environment } from 'src/environments/environment';
import { MatIcon } from '@angular/material/icon';

export interface DonutSection {
  label: string;
  value: number;
  color?: string;
  featured?: boolean;
  shortLabel?: string;
  click?: () => void;
}

const AVAILABLE_COLORS = [
  environment.colors.primary,
  '#2EC229',
  '#4832C2',
  '#F40320',
  '#C20C70',
  '#C23D30',
  '#C149A1',
];

@Component({
  selector: 'qa-donut-chart',
  standalone: true,
  imports: [NgChartsModule, TranslateModule, MatIcon],
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DonutChartComponent implements OnChanges {
  @Input() sections: DonutSection[] = [];
  @Input() sort = false;
  @Input() cutout = 85;
  @Input() showProgressBar = false;

  @HostBinding('class.large')
  @Input()
  large = false;

  featureValue?: number;
  featureLabel?: string;

  doughnutData: ChartConfiguration<'doughnut'>['data'] = {
    labels: [],
    datasets: [],
  };

  doughnutOptions: ChartOptions<'doughnut'>;

  constructor(elementRef: ElementRef<HTMLElement>) {
    elementRef.nativeElement.classList.add('qa-donut-chart');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections']) {
      this.buildChart();
    }
  }

  private buildChart(): void {
    this.doughnutOptions = {
      responsive: true,
      cutout: `${this.cutout}%`,
      circumference: 360,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
    };
    let data = cloneDeep(this.sections || []);

    if (this.sort) {
      data.sort((a, b) => b.value - a.value);
    }

    const total = data.reduce((a, b) => a + b.value, 0);

    this.doughnutData = {
      labels: data.map((s) => s.label),
      datasets: [
        {
          data: data.map((s) => s.value),
          backgroundColor: data.map(
            (s, i) => s.color || AVAILABLE_COLORS[i % AVAILABLE_COLORS.length]
          ),
          borderWidth: 0,
        },
      ],
    };

    const featured = data.find((s) => s.featured);
    if (!isNil(featured?.value)) {
      this.featureValue = Math.round((featured.value / total) * 100);
      this.featureLabel = featured.shortLabel;
    } else {
      this.featureValue = undefined;
      this.featureLabel = undefined;
    }
  }

  color(section: DonutSection): string {
    const idx = this.sections.indexOf(section);
    return AVAILABLE_COLORS[idx % AVAILABLE_COLORS.length];
  }

  calculatePercentage(section: DonutSection): number {
    const total = this.sections.reduce((a, b) => a + b.value, 0);
    return total > 0 ? (section.value / total) * 100 : 0;
  }
}
